/**
 * External dependencies
 */
import { useState, useCallback, useEffect, useMemo } from "react";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";

/**
 * Internal dependencies
 */
import {
	decodeSafeSpecialCharacters,
	getArrayFromUncertainData,
	getQueryStringValue,
	setQueryStringValue,
	stringifyForApi,
} from "./utils";
import { Term, Filters, Page } from "./api/types";
import { FILTERS_API_URL, POSTS_API_URL } from "./api/constants";
import { fetcher } from "./utils";
import config from './config';

export function useUrlParams<T>(key: string, initialValue: T) {
	const [value, setValue] = useState(
		getQueryStringValue(key, Array.isArray(initialValue)) || initialValue
	);

	const setValueAndQueryString = useCallback(
		(newValue: T) => {
			setValue(newValue);
			setQueryStringValue(key, newValue);
		},
		[key]
	);

	return [value, setValueAndQueryString] as [T, (newValue: T) => void];
}

export function useUrlParamState<T>(key: string, initialValue: T) {
	const [param, setParam] = useUrlParams(key, initialValue);
	const [value, setValue] = useState(param);

	useEffect(() => {
		setParam(value);
	}, [value, setParam]);

	return [value, setValue] as [T, React.Dispatch<T>];
}

export function useFilters() {

	const { data, ...rest } = useSWR<Filters>(`${FILTERS_API_URL}&lang=${config.text.CurrentLangSlug}`, fetcher);

	/**
	 * For an unknown reason, the filters data is randomly being
	 * returned as an indexed object instead of an array.
	 * This is a temporary fix to ensure the data is always an array.
	 */
	const filters = useMemo(() => {
		if (!data) {
			return undefined;
		}

		return Object.entries(data).reduce((result, [key, value]) => {
			return {
				...result,
				[key]: getArrayFromUncertainData(value),
			};
		}, {} as Filters<Term[]>);
	}, [data]);

	return {
		data: filters,
		...rest,
	};
}

export function usePosts({ perPage = 10, queryParams = {} }) {
	/**
	 * Fetch pages of posts
	 */
	const {
		data: pages,
		error,
		size,
		setSize,
		...rest
	} = useSWRInfinite<Page>((pageIndex, previousPageData) => {
		if (previousPageData && !previousPageData.length) {
			return null;
		}

		const url = [
			POSTS_API_URL,
			`page=${pageIndex + 1}`,
			`per_page=${perPage}`,
			`lang=${config.text.CurrentLangSlug}`,
			stringifyForApi(queryParams),
		];

		return url.join("&");
	}, fetcher);

	/**
	 * Load more pages
	 */
	const isLoadingFirstPage = !pages && !error;
	const isLoadingMore = !!(
		size > 0 &&
		pages &&
		typeof pages[size - 1] === "undefined"
	);
	const isEmpty = pages?.[0]?.length === 0;
	const isLastPage =
		isEmpty || (pages && pages[pages.length - 1]?.length < perPage);
	const canLoadMore = !isLoadingFirstPage && !isLastPage && !isLoadingMore;

	const loadMore = () => {
		setSize(size + 1);
	};

	/**
	 * Flatten pages to posts
	 */
	const posts = Array.isArray(pages) ? pages?.flat() : [];

	return {
		data: posts,
		error,
		size,
		setSize,
		canLoadMore,
		isLoadingMore,
		loadMore,
		isEmpty,
		...rest,
	};
}

export function useTermsAsSelectOptions(data?: Term[]) {
	const selectOptions = useMemo(() => {
		if (!data) {
			return [];
		}

		return data.map((term) => ({
			label: decodeSafeSpecialCharacters(term.name),
			value: term.id,
		}));
	}, [data]);

	return selectOptions;
}
